/* eslint-disable jsx-a11y/no-autofocus */
import React, { useRef, useState } from 'react';
import { Link } from '../root/components/Link';
import { CheckboxWithTerms } from './CheckboxWithTerms';
import { CustomerDetails } from '../models/CustomerDetails.model';
import styles from './LoyaltyForm.module.scss';
import { Button } from '../root/components/buttons/Button';

interface Props {
  submit: (customerDetails: CustomerDetails) => void;
  initialDetails?: CustomerDetails;
}

export const LoyaltySignupForm: React.FC<Props> = ({ initialDetails, submit }) => {
  const [firstName, setFirstName] = useState<string>(initialDetails?.firstName || '');
  const [lastName, setLastName] = useState<string>(initialDetails?.lastName || '');
  const [emailAddress, setEmailAddress] = useState<string>(initialDetails?.emailAddress || '');
  const [phoneNumber, setPhoneNumber] = useState<string>(initialDetails?.phoneNumber || '');
  const [tosChecked, setTosChecked] = useState<boolean>(!!initialDetails);
  const [phoneMarketingChecked, setPhoneMarketingChecked] = useState<boolean>(!!initialDetails?.phoneMarketingChecked);
  const [emailMarketingChecked, setEmailMarketingChecked] = useState<boolean>(!!initialDetails?.emailMarketingChecked);

  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const hasRequiredFields = firstName && lastName && (emailAddress || phoneNumber) && tosChecked;
  const emailInvalid = !!emailAddress && !emailRef?.current?.validity?.valid;
  const phoneInvalid = !!phoneNumber && !phoneRef?.current?.validity?.valid;

  const disableSignUpButton = !hasRequiredFields || emailInvalid || phoneInvalid;

  return (
    <>
      <input
        autoFocus={true}
        ref={emailRef}
        pattern="^(.+@\w+(\.\w+)+)$"
        type="email"
        placeholder={'Email'}
        value={emailAddress}
        onChange={(e) => setEmailAddress(e.target.value)}
      />
      <input
        type="tel"
        pattern="^\d{10}$"
        ref={phoneRef}
        placeholder={'Phone number'}
        value={phoneNumber}
        onKeyPress={(e) => {
          if (isNaN(e.key as unknown as number)) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          setPhoneNumber(e.target.value);
        }}
      />
      <div className={styles.nameInputWrapper}>
        <input type="text" placeholder={'First name'} value={firstName} onChange={(e) => setFirstName(e.target.value ?? '')} />
        <input type="text" placeholder={'Last name'} value={lastName} onChange={(e) => setLastName(e.target.value ?? '')} />
      </div>
      <CheckboxWithTerms checked={emailMarketingChecked} setChecked={setEmailMarketingChecked} terms={'Send me important notifications, news, and offers'} />
      <CheckboxWithTerms
        checked={phoneMarketingChecked}
        setChecked={setPhoneMarketingChecked}
        terms={'Send me program updates through text*'}
        legalInfo={
          <span className={styles.inputLegal}>
            *I agree to receive recurring automated marketing text messages (e.g. tier updates) at the phone number provided. Consent is not a condition to use
            the program. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help and STOP to cancel. View our{' '}
            <Link dataTrackLabel="Privacy Policy" dataTrackLocation="footer" href="/pages/privacy-policy" buttonType="ghost">
              privacy policy
            </Link>
            {' and '}
            <Link dataTrackLabel="Terms of Service" dataTrackLocation="footer" href="/pages/terms-of-service" buttonType="ghost">
              terms of service
            </Link>
            .
          </span>
        }
      />
      <CheckboxWithTerms
        checked={tosChecked}
        setChecked={setTosChecked}
        terms={'Required: Accept Terms & Conditions'}
        legalInfo={
          <span className={styles.inputLegal}>
            Honeylove Hive is for U.S. customers only. By creating an account, you affirm that you have read, understood, and consent to the&nbsp;
            <Link dataTrackLabel="Privacy Policy" dataTrackLocation="footer" href="/pages/privacy-policy" buttonType="ghost">
              privacy policy
            </Link>
            ,&nbsp;
            <Link dataTrackLabel="Terms of Service" dataTrackLocation="footer" href="/pages/terms-of-service" buttonType="ghost">
              terms of service
            </Link>
            &nbsp;, and the&nbsp;
            <Link href="/pages/honeylove-hive-terms" buttonType="ghost">
              Honeylove Hive Terms &amp; conditions
            </Link>
            .
          </span>
        }
      />
      <Button
        disabled={disableSignUpButton}
        onClick={() => submit({ firstName, lastName, emailAddress, phoneNumber, phoneMarketingChecked, emailMarketingChecked })}
        dataTrackLabel="Sign up"
        dataTrackLocation="Loyalty sign up page"
        fullWidth={true}
        text={'Sign up'}
        size="large"
      />
    </>
  );
};

export default LoyaltySignupForm;
